<script lang="ts">
    import { Component, Prop, Vue } from 'vue-facing-decorator';
    import { DateTimeUtil, IconName, SearchQASelectors, TranslationKey, TranslationService } from 'webshop-modules';
    import {
        ArtistSearchResult,
        DateTimeZone,
        SearchResult,
        SearchResultEvents,
        SearchSuggestion,
        SearchSuggestionOptions,
    } from 'webshop-api';
    import { useAppliedFiltersStore } from '@/domain/search/application/AppliedFilters.store';
    import { useSearchResultsStore } from '@/domain/search/application/SearchResults.store';
    import IconComponent from '../atoms/Icon.vue';

    @Component({
    name: "SearchInputComponent",
    components: { 
        IconComponent 
    },
})
export default class SearchInputComponent extends Vue {
    @Prop({ type: String })
    public label: string | undefined;

    @Prop({ type: Boolean })
    public artistSearch: boolean | undefined;

    @Prop({ type: String })
    public name!: string;

    @Prop({ type: String })
    public icon!: IconName;

    public readonly SearchQASelectors: typeof SearchQASelectors = SearchQASelectors;
    public searchTranslation = '';

    private AppliedFilters = useAppliedFiltersStore();
    private SearchResults = useSearchResultsStore();

    public value: string = this.AppliedFilters.searchText !== undefined
        ? this.AppliedFilters.searchText
        : '';

    public get quickSearchList(): Array<SearchResult> {
        return this.SearchResults.quickSearchList.results ?? [];
    }

    public get quickSearchArtistsList(): Array<ArtistSearchResult> {
        return this.SearchResults.quickSearchArtistsList.results ?? [];
    }

    public get quickSearchSuggestions(): Array<SearchSuggestion> {
        return this.SearchResults.quickSearchList.suggestions ?? [];
    }

    public get quickSearchArtistsSuggestions(): Array<SearchSuggestion> {
        return this.SearchResults.quickSearchArtistsList.suggestions ?? [];
    }

    public handleSubmit(): void {
        this.hideQuickSearch = true;
        const target: HTMLInputElement = document.getElementById(this.name) as HTMLInputElement;

        if (!this.artistSearch) {
            this.AppliedFilters.updateAppliedFilters({ searchText: target.value });
            this.SearchResults.getData(false, this.$apiService);
        } else {
            this.AppliedFilters.updateAppliedFilters({ searchText: target.value });
            this.SearchResults.getArtists(false, this.$apiService);
        }
    }

    public handleQuickSearchSubmit(val: string): void {
        if (!this.artistSearch) {
            this.hideQuickSearch = true;
            this.value = val;

            this.AppliedFilters.updateAppliedFilters({ searchText: val });
            this.SearchResults.getData(false, this.$apiService);
        }
    }

    public handleInput(value: string): void {
        if (value !== '' && !this.artistSearch) {
            this.SearchResults.getData({ searchText: value }, this.$apiService);
        }
    }

    public readonly iconName: typeof IconName = IconName;

    public readonly Translation: typeof TranslationKey = TranslationKey;

    public placeholderState: boolean = this.value === undefined || this.value === '';

    public hideQuickSearch = false;

    public suggestion: string[] = [];

    public get showQuickSearch(): boolean {
        if (this.hideQuickSearch) {
            return false;
        }

        if (this.value && this.value.length > 1) {
            if (this.quickSearchSuggestions || this.quickSearchArtistsSuggestions) {
                return this.hasSuggestions();
            }
        }

        return false;
    }

    public get getIconName(): IconName {
        return this.icon ? this.icon : IconName.ArrowFull;
    }

    public compileSuggestions(): void {
        const options: string[] = [];

        if (this.quickSearchSuggestions) {
            if (this.value && this.value.split(' ').length) {

                this.quickSearchSuggestions.forEach((type: SearchSuggestion) => {
                    if (type.options && type.options.length) {
                        type.options.forEach((option: SearchSuggestionOptions) => {
                            if (option.text) {
                                options.push(option.text);
                            }
                        });
                    }

                    this.suggestion = [...new Set(options)];
                });
            }
        }
    }

    public hasSuggestions(): boolean {
        if (this.quickSearchSuggestions && this.quickSearchArtistsSuggestions) {
            this.compileSuggestions();

            return (
                this.quickSearchSuggestions.reduce((total: number, item: SearchSuggestion) => {
                    const itemOptionsLength = item.options?.length ?? 0;

                    return total + itemOptionsLength
                }, 0) >
                    0 ||
                this.quickSearchArtistsSuggestions.reduce(
                    (total: number, item: SearchSuggestion) => {
                    const itemOptionsLength = item.options?.length ?? 0;

                    return total + itemOptionsLength
                },
                    0
                ) > 0
            );
        } else {
            return (
                (this.quickSearchList && this.quickSearchList.length > 0) ||
                (this.quickSearchArtistsList && this.quickSearchArtistsList.length > 0)
            );
        }
    }

    public addSearchTermToUrl(url: string): string {
        return `${url}?searchText=${this.value}`;
    }

    public getCities(events: SearchResultEvents[]): string | undefined {
        if (events.length === 1) {
            return events[0].city;
        } else {
            const allCities = events.map(item => {
                return item.city;
            });
            const distinctCities = [...new Set(allCities)];
            return distinctCities.join(' / ');
        }
    }

    public earliestStartDateNumeric(events: SearchResultEvents[]): string {
        if (events.length === 1 && events[0].timezone) {
            return DateTimeUtil.getDateTimeNumericFormatFor(
                TranslationService.getLanguageCode(),
                events[0].timezone
            ).format(new Date((events[0].dateStart as unknown as DateTimeZone).dateTime));
        } else {
            const allStartDates = events.map(item => {
                return item.dateStart as unknown as DateTimeZone;
            });
            const distinctStartDates = [...new Set(allStartDates)];

            if (distinctStartDates.length === 1 && events[0].timezone) {
                return DateTimeUtil.getDateTimeNumericFormatFor(
                    TranslationService.getLanguageCode(),
                    events[0].timezone
                ).format(new Date((events[0].dateStart as unknown as DateTimeZone).dateTime));
            } else {
                // get earliest
                return DateTimeUtil.getDateTimeNumericFormatFor(
                    TranslationService.getLanguageCode(),
                    events[0].timezone ?? ""
                ).format(
                    new Date(
                        Math.min.apply(
                            null,
                            distinctStartDates.map(e => Number(new Date(e.dateTime)))
                        )
                    )
                );
            }
        }
    }

    public latestEndDateNumeric(events: SearchResultEvents[]): string {
        if (events.length === 1 && events[0].timezone) {
            return DateTimeUtil.getDateTimeNumericFormatFor(
                TranslationService.getLanguageCode(),
                events[0].timezone
            ).format(new Date((events[0].dateEnd as unknown as DateTimeZone).dateTime));
        } else {
            const allEndDates = events.map(item => {
                return item.dateEnd as unknown as DateTimeZone;
            });
            const distinctEndDates = [...new Set(allEndDates)];

            if (distinctEndDates.length === 1 && events[0].timezone) {
                return DateTimeUtil.getDateTimeNumericFormatFor(
                    TranslationService.getLanguageCode(),
                    events[0].timezone
                ).format(new Date(distinctEndDates[0].dateTime));
            } else {
                // get latest
                return DateTimeUtil.getDateTimeNumericFormatFor(
                    TranslationService.getLanguageCode(),
                    events[0].timezone ?? ""
                ).format(
                    new Date(
                        Math.max.apply(
                            null,
                            distinctEndDates.map(e => Number(new Date(e.dateTime)))
                        )
                    )
                );
            }
        }
    }

    public hasDateSpan(start: string, end: string): boolean {
        return start !== end;
    }

    private async created() {
        window.addEventListener('scroll', () => (this.hideQuickSearch = true));
        this.searchTranslation = await this.$translationService.getTranslationFor(TranslationKey.Search);
    }

    private mounted() {
        (this.$refs.quickSearchDropdown as HTMLElement).addEventListener(
            'mouseleave',
            () => (this.hideQuickSearch = true)
        );
    }
}
</script>

<template>
    <div class="a-formField has-quickSearch">
        <transition name="fade">
            <label class="a-formField__placeholder" v-if="label && placeholderState" :for="name">{{ label }}</label>
        </transition>

        <button
            class="a-formField__action a-button -simple"
            type="submit"
            v-bind:aria-label="searchTranslation"
            v-on:click="handleSubmit()">
            <IconComponent :icon-id="getIconName" :data-qa="SearchQASelectors.SEARCH_ICON" />
            <span class="a-hidden">{{ searchTranslation }}</span>
        </button>

        <input
            :class="['a-formInput', { hasSuggestions: showQuickSearch }]"
            type="text"
            ref="searchInput"
            v-debounce:300="handleInput"
            v-on:click="placeholderState = false"
            v-on:blur="placeholderState = value === ''"
            v-on:keyup.enter="handleSubmit()"
            v-on:focusin="hideQuickSearch = false"
            :id="name"
            :name="name"
            v-model="value"
            autocomplete="off"
            data-js="artists-search-input" />

        <div class="m-quickSearch" ref="quickSearchDropdown" v-show="showQuickSearch">
            <template v-if="suggestion">
                <ul v-for="s in suggestion" v-bind:key="s">
                    <li class="m-quickSearch__item -suggestion" v-on:click="handleQuickSearchSubmit(s)">
                        <IconComponent :icon-id="iconName.Search" /> <span>{{ s }}</span>
                    </li>
                </ul>
            </template>
            <template v-if="quickSearchList">
                <ul v-for="item in quickSearchList.slice(0, 3)" v-bind:key="item.title">
                    <li class="m-quickSearch__item">
                        <a :href="addSearchTermToUrl(item.url ?? '')">
                            <IconComponent :icon-id="iconName.Calendar" />
                            <span>{{ item.title }}</span>
                            <span v-if="item.events">, {{ getCities(item.events) }} </span>
                            <span>({{ earliestStartDateNumeric(item.events ?? []) }}</span>
                            <span
                                v-if="
                                    hasDateSpan(
                                        earliestStartDateNumeric(item.events ?? []),
                                        latestEndDateNumeric(item.events ?? [])
                                    )
                                ">
                                - {{ latestEndDateNumeric(item.events ?? []) }}</span
                            >
                            <span>)</span>
                        </a>
                    </li>
                </ul>
            </template>
            <template v-if="quickSearchArtistsList">
                <ul v-for="item in quickSearchArtistsList.slice(0, 3)" v-bind:key="item.title">
                    <li class="m-quickSearch__item">
                        <a :href="addSearchTermToUrl(item.url ?? '')">
                            <IconComponent :icon-id="iconName.Calendar" />
                            <span>{{ item.title }}</span>
                        </a>
                    </li>
                </ul>
            </template>
        </div>
    </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}
</style>
